import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import { InvitationInfo, InvitationAcceptRequest } from '../types/api';
import Logger from '../utils/logger';
import { VERSION_CONDITIONS, URL_CONDITIONS, URL_CONFIDENTIALITE } from '../constants/legal';

const InvitationAcceptPage: React.FC = () => {
  const { t } = (useTranslation as any)('invitation');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState<InvitationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    mot_de_passe: '',
    confirmPassword: '',
    conditions: false
  });

  useEffect(() => {
    if (token) {
      fetchInvitationInfo();
    }
  }, [token]);

  const fetchInvitationInfo = async () => {
    try {
      const invitationData = await apiService.getInvitationInfo(token!);
      setInvitation(invitationData);
    } catch (error) {
      Logger.error('Failed to load invitation');
      toast.error(t('notFoundOrExpired'));
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!invitation?.user_exists && formData.mot_de_passe !== formData.confirmPassword) {
      toast.error(t('errors.passwordMismatch'));
      return;
    }

    if (!invitation?.user_exists) {
      if (formData.mot_de_passe.length < 8) {
        toast.error(t('errors.passwordMinLength'));
        return;
      }
      if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.mot_de_passe)) {
        toast.error(t('errors.passwordComplexity'));
        return;
      }
    }

    try {
      setAccepting(true);

      const acceptData: InvitationAcceptRequest = {
        token_invitation: token!,
        ...(invitation?.user_exists ? {} : {
          nom: formData.nom,
          prenom: formData.prenom,
          mot_de_passe: formData.mot_de_passe,
          conditions_version: VERSION_CONDITIONS
        })
      };

      const response = await apiService.acceptInvitation(acceptData);

      toast.success(response.message);

      // Backend sets httpOnly cookie; we only track auth state
      if (response.access_token) {
        localStorage.setItem('auth_status', 'true');
      }

      // Rediriger vers le projet
      navigate(`/projects/${response.projet_id}`);

    } catch (error) {
      Logger.error('Failed to accept invitation');
      toast.error(t('errors.acceptError'));
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-0 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500"></div>
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="min-h-screen bg-surface-0 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-text-primary mb-4">{t('notFound')}</h1>
          <p className="text-text-secondary mb-4">{t('notFoundDesc')}</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-accent-500 hover:bg-accent-700 text-white font-bold py-2 px-4 rounded"
          >
            {t('login')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-0 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-text-primary">{t('title')}</h1>
          <p className="mt-2 text-text-secondary" dangerouslySetInnerHTML={{ __html: t('invitedTo', { project: invitation.project_name }) as string }}>
          </p>
        </div>

        <div className="bg-surface-2 p-6 rounded-card shadow-card">
          <div className="mb-6">
            <h3 className="text-lg font-medium text-text-primary mb-2">{t('details.title')}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-text-secondary">{t('details.email')}</span>
                <span className="font-medium">{invitation.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-secondary">{t('details.role')}</span>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  invitation.role === 'admin' ? 'bg-red-100 text-red-800' :
                  invitation.role === 'developer' ? 'bg-accent-100 text-accent-800' :
                  'bg-green-100 text-green-800'
                }`}>
                  {invitation.role}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-secondary">{t('details.project')}</span>
                <span className="font-medium">{invitation.project_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-secondary">{t('details.expiresAt')}</span>
                <span className="font-medium">
                  {new Date(invitation.expires_at).toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <form onSubmit={handleAcceptInvitation}>
            {!invitation.user_exists && (
              <div className="mb-6">
                <h4 className="text-md font-medium text-text-primary mb-3">
                  {t('createAccount')}
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary">{t('firstName')}</label>
                    <input
                      type="text"
                      required
                      value={formData.prenom}
                      onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-border-1 rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary">{t('lastName')}</label>
                    <input
                      type="text"
                      required
                      value={formData.nom}
                      onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-border-1 rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary">{t('password')}</label>
                    <input
                      type="password"
                      required
                      minLength={8}
                      value={formData.mot_de_passe}
                      onChange={(e) => setFormData({ ...formData, mot_de_passe: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-border-1 rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary">{t('confirmPassword')}</label>
                    <input
                      type="password"
                      required
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-border-1 rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                    />
                  </div>
                </div>

                {/* Acceptation des conditions. Uniquement dans la branche « nouvel
                    utilisateur » : accepter une invitation sans compte en CREE un, c'est
                    le meme acte qu'une inscription. Un membre deja inscrit n'a rien a
                    reaccepter -- le lui redemander a chaque invitation recue serait
                    absurde. */}
                <div className="mt-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      required
                      checked={formData.conditions}
                      onChange={(e) => setFormData({ ...formData, conditions: e.target.checked })}
                      className="mt-0.5 h-4 w-4 rounded border-border-1 text-accent-500 focus:ring-accent-500"
                    />
                    <span className="text-xs text-text-secondary">
                      {t('terms.text')}{' '}
                      <a href={URL_CONDITIONS} target="_blank" rel="noopener noreferrer"
                         className="font-medium text-accent-600 hover:text-accent-500">
                        {t('terms.termsOfService')}
                      </a>{' '}
                      <span className="text-text-tertiary">
                        ({t('terms.version', { version: VERSION_CONDITIONS })})
                      </span>{' '}
                      {t('terms.and')}{' '}
                      <a href={URL_CONFIDENTIALITE} target="_blank" rel="noopener noreferrer"
                         className="font-medium text-accent-600 hover:text-accent-500">
                        {t('terms.privacyPolicy')}
                      </a>
                    </span>
                  </label>
                </div>
              </div>
            )}

            {invitation.user_exists && (
              <div className="mb-6 p-4 bg-accent-500/12 border border-accent-200 rounded-md">
                <p className="text-sm text-accent-800">
                  {t('existingAccount')}
                </p>
              </div>
            )}

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="flex-1 py-2 px-4 border border-border-1 rounded-md text-sm font-medium text-text-secondary hover:bg-surface-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
              >
                {t('cancel')}
              </button>

              <button
                type="submit"
                disabled={accepting}
                className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent-600 hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 disabled:opacity-50"
              >
                {accepting ? t('accepting') : t('accept')}
              </button>
            </div>
          </form>
        </div>

        <div className="text-center">
          <p className="text-sm text-text-secondary">
            {t('acceptDisclaimer', { role: invitation.role })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default InvitationAcceptPage;
